// Where a site walk went, for the map beside its video (Phelipe 2026-09-25:
// "if the user uploaded the .insv, show where they walked on the map").
// Pure helpers, no DOM. A route is [[seconds since filming started, lat, lon], ...]
// in time order (SiteWalkPlayback.gps_track). Tested in
// test/javascript/site_walk_route_test.mjs.

// How far the route's seconds can differ from the copy's own length and
// still be the same stretch of walking (a tenth).
const SAME_STRETCH = 0.1

// The pace of a walk's copy against its route: `frameSeconds` each picture
// of a timelapse is held, `intervalSeconds` the camera's own spacing, and
// where the pictures sit on the route's clock. The route's seconds count from
// the camera's stamp, and the phone's first fix comes a little after it
// (Larry's Curb & Gutter walk, K3-33 2026-10-08: 40 s), while the pictures
// come a touch faster than the camera's "every 1.03 s" (963 in 939 s).
// Picture 0 at route second 0 at the nominal pace put the dot 40 m behind
// for most of the walk and past the end at the finish, the fan with it. When
// the pictures (or an ordinary video, `videoDuration` long) and the route
// plainly cover the same stretch, the pictures are spread over the route:
// `offset` is its first second, `stretch` its length over the nominal one.
// Otherwise (the phone stopped logging halfway) both stay neutral.
export function walkPace(walk, videoDuration) {
  const frameSeconds = Number(walk?.frame_seconds) || 0
  const intervalSeconds = (Number(walk?.capture_interval_ms) || 0) / 1000
  const pace = { frameSeconds, intervalSeconds, offset: 0, stretch: 1 }
  const route = Array.isArray(walk?.route) ? walk.route : null
  if (!route || route.length < 2) return pace
  const span = Number(route[route.length - 1][0]) - Number(route[0][0])
  const pictures = Number(walk.picture_count) || 0
  const nominal = frameSeconds > 0 && intervalSeconds > 0
    ? (pictures > 1 ? (pictures - 1) * intervalSeconds : 0)
    : (Number(videoDuration) || 0)
  // The camera's own count of the walk's seconds (a timelapse) is the surer
  // check that the pictures and the fixes cover the same stretch.
  const walked = Number(walk.real_duration_seconds) || nominal
  if (!(span > 0) || !(nominal > 0) || Math.abs(walked - span) > span * SAME_STRETCH) return pace
  pace.offset = Number(route[0][0])
  pace.stretch = span / nominal
  return pace
}

// Seconds on the route of the frame the video shows at `videoTime` (see
// walkPace). A timelapse copy holds each picture for `frameSeconds`, and the
// pictures were taken `intervalSeconds` apart; ordinary video runs at the
// pace it was filmed.
export function filmedSecondsAt(videoTime, { frameSeconds = 0, intervalSeconds = 0, offset = 0, stretch = 1 } = {}) {
  const v = Math.max(0, Number(videoTime) || 0)
  const nominal = frameSeconds > 0 && intervalSeconds > 0 ? Math.floor(v / frameSeconds) * intervalSeconds : v
  return offset + nominal * stretch
}

// The video time that shows `filmedSeconds` of the route - the middle of the
// picture for a timelapse, where Walk mode lands too - never past the end.
export function videoTimeFor(filmedSeconds, { frameSeconds = 0, intervalSeconds = 0, offset = 0, stretch = 1, duration = Infinity } = {}) {
  const f = Math.max(0, ((Number(filmedSeconds) || 0) - offset) / (stretch || 1))
  let v = f
  if (frameSeconds > 0 && intervalSeconds > 0) v = Math.round(f / intervalSeconds) * frameSeconds + frameSeconds / 2
  const end = Number.isFinite(duration) && duration > 0 ? Math.max(0, duration - 0.05) : Infinity
  return Math.min(v, end)
}

// Fix the dot (pmfit K9-1, Phelipe 2026-10-17): a walk's GPS can be a few
// meters off, so its dot runs a little ahead or behind. Someone paused where
// they knew the spot and clicked it on the map: `fixes` are those
// [video seconds, route seconds] pairs, and `baseAt(v)` the route seconds the
// walk's own timing gives at video time v. The shift (route seconds to add)
// is each fix's own before the first and after the last, and blends from one
// fix to the next in between - one fix moves the whole walk.
export function fixShift(videoSeconds, fixes, baseAt) {
  const list = (Array.isArray(fixes) ? fixes : [])
    .filter((f) => Array.isArray(f) && Number.isFinite(Number(f[0])) && Number.isFinite(Number(f[1])))
    .map(([v, r]) => [ Number(v), Number(r) - baseAt(Number(v)) ])
    .sort((a, b) => a[0] - b[0])
  if (!list.length) return 0
  const v = Number(videoSeconds) || 0
  if (v <= list[0][0]) return list[0][1]
  const last = list[list.length - 1]
  if (v >= last[0]) return last[1]
  for (let i = 1; i < list.length; i++) {
    const [v0, d0] = list[i - 1]
    const [v1, d1] = list[i]
    if (v <= v1) return v1 === v0 ? d1 : d0 + (d1 - d0) * (v - v0) / (v1 - v0)
  }
  return last[1]
}

// The video time showing `routeSeconds` once the fixes apply: `videoFor(r)`
// is the walk's own answer; a few rounds settle where the shift changes.
export function fixedVideoTime(routeSeconds, fixes, baseAt, videoFor) {
  let v = videoFor(routeSeconds)
  if (!Array.isArray(fixes) || !fixes.length) return v
  for (let i = 0; i < 4; i++) v = videoFor(routeSeconds - fixShift(v, fixes, baseAt))
  return v
}

// The route point closest in time to `seconds`, or -1 for no route.
export function indexAtTime(route, seconds) {
  if (!Array.isArray(route) || route.length === 0) return -1
  let lo = 0
  let hi = route.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (route[mid][0] < seconds) lo = mid + 1
    else hi = mid
  }
  if (lo > 0 && Math.abs(route[lo - 1][0] - seconds) <= Math.abs(route[lo][0] - seconds)) return lo - 1
  return lo
}

// The route point nearest a place on the map, or -1 for no route.
export function indexNear(route, lat, lng) {
  if (!Array.isArray(route) || route.length === 0) return -1
  const squeeze = Math.cos((Number(lat) * Math.PI) / 180) // a degree of longitude shrinks away from the equator
  let best = -1
  let bestDistance = Infinity
  route.forEach(([, pointLat, pointLng], i) => {
    const dy = pointLat - lat
    const dx = (pointLng - lng) * squeeze
    const distance = dx * dx + dy * dy
    if (distance < bestDistance) {
      bestDistance = distance
      best = i
    }
  })
  return best
}

// The see-through fan drawn from the dot on the map, pointing up (north) in a
// square 2 × radius wide, `spread` degrees across (#5, like DroneDeploy's
// view cone); the map turns it to the way the 360° view looks. SVG path data.
export function fanPath(spread, radius) {
  const half = (Math.max(10, Math.min(170, Number(spread) || 0)) * Math.PI) / 360
  const r = radius
  const point = (angle) => `${(r + r * Math.sin(angle)).toFixed(2)} ${(r - r * Math.cos(angle)).toFixed(2)}`
  return `M ${r} ${r} L ${point(-half)} A ${r} ${r} 0 0 1 ${point(half)} Z`
}
